import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { SettingsMenuGroup, SettingsTab, VariablesSubTab, EmailDocsSubTab } from './settings-nav-shared';
import { VARIABLES_SUB_ITEMS, EMAIL_DOCS_SUB_ITEMS, settingsNavItemMatchesFilter } from './settings-nav-shared';

type Props = {
  variant: 'sidebar' | 'sheet';
  groups: SettingsMenuGroup[];
  navFilter: string;
  onNavFilterChange: (value: string) => void;
  navFilterLower: string;
  labelMatchesFilter: (labelKey: string) => boolean;
  settingsTab: SettingsTab;
  variablesSubTab: VariablesSubTab;
  emailDocsSubTab: EmailDocsSubTab;
  onSelectTab: (tab: SettingsTab) => void;
  onSelectVariablesSub: (sub: VariablesSubTab) => void;
  onSelectEmailDocsSub: (sub: EmailDocsSubTab) => void;
  onCloseSheet?: () => void;
};

export function SettingsNavPanel({
  variant,
  groups,
  navFilter,
  onNavFilterChange,
  navFilterLower,
  labelMatchesFilter,
  settingsTab,
  variablesSubTab,
  emailDocsSubTab,
  onSelectTab,
  onSelectVariablesSub,
  onSelectEmailDocsSub,
  onCloseSheet,
}: Props) {
  const { t } = useTranslation();
  const sheet = variant === 'sheet';

  const handlePrimaryClick = (itemValue: SettingsTab, isVariables: boolean, isEmailDocs: boolean) => {
    onCloseSheet?.();
    if (isVariables) {
      onSelectTab('variables');
      onSelectVariablesSub('product-types');
    } else if (isEmailDocs) {
      onSelectTab('email-and-docs');
      onSelectEmailDocsSub('email-templates');
    } else {
      onSelectTab(itemValue);
    }
  };

  return (
    <>
      <Input
        value={navFilter}
        onChange={(e) => onNavFilterChange(e.target.value)}
        placeholder={t('settings.menuSearchPlaceholder')}
        className={cn(sheet ? 'h-9 text-sm' : 'mb-3 h-9 text-sm')}
        aria-label={t('settings.menuSearchPlaceholder')}
      />
      {groups.map((group) => {
        const visibleItems = group.items.filter((item) =>
          settingsNavItemMatchesFilter(item, navFilterLower, labelMatchesFilter)
        );
        if (visibleItems.length === 0) return null;
        return (
          <div
            key={group.id}
            className={cn(sheet ? 'space-y-1' : 'mb-4 last:mb-2')}
          >
            <div
              className={cn(
                'text-xs font-semibold text-muted-foreground uppercase tracking-wide px-1',
                sheet ? '' : 'mb-1.5'
              )}
            >
              {t(group.labelKey)}
            </div>
            <div className="space-y-0.5">
              {visibleItems.map((item) => {
                const isVariables = item.value === 'variables';
                const isEmailDocs = item.value === 'email-and-docs';
                const primaryActive = settingsTab === item.value;

                return (
                  <div key={item.value}>
                    <button
                      type="button"
                      onClick={() => handlePrimaryClick(item.value, isVariables, isEmailDocs)}
                      className={cn(
                        sheet
                          ? 'flex items-center gap-2 w-full p-2 rounded-md text-left text-sm hover:bg-muted'
                          : 'flex items-center gap-2 w-full p-1.5 rounded-md cursor-pointer hover:bg-muted transition-colors text-left text-foreground',
                        primaryActive && 'bg-primary/10 font-medium text-primary'
                      )}
                    >
                      <ChevronRight className="h-4 w-4 shrink-0" />
                      <span className={cn('text-sm', sheet ? 'flex-1' : 'flex-1')}>{t(item.labelKey)}</span>
                    </button>
                    {!sheet && isVariables && settingsTab === 'variables' && (
                      <div className="pl-5 pr-1.5 space-y-0.5">
                        {VARIABLES_SUB_ITEMS.filter(
                          (sub) =>
                            !navFilterLower ||
                            labelMatchesFilter(sub.labelKey) ||
                            labelMatchesFilter('settings.catalogAndContent')
                        ).map((sub) => {
                          const subSelected = variablesSubTab === sub.value;
                          return (
                            <button
                              key={sub.value}
                              type="button"
                              onClick={() => onSelectVariablesSub(sub.value)}
                              className={cn(
                                'flex items-center gap-2 w-full py-1.5 rounded-md cursor-pointer hover:bg-muted transition-colors text-left text-foreground',
                                subSelected && 'bg-primary/10 font-medium text-primary'
                              )}
                            >
                              <ChevronRight className="h-4 w-4 shrink-0" />
                              <span className="text-sm flex-1">{t(sub.labelKey)}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {!sheet && isEmailDocs && settingsTab === 'email-and-docs' && (
                      <div className="pl-5 pr-1.5 space-y-0.5">
                        {EMAIL_DOCS_SUB_ITEMS.filter(
                          (sub) =>
                            !navFilterLower ||
                            labelMatchesFilter(sub.labelKey) ||
                            labelMatchesFilter('settings.emailAndDocs')
                        ).map((sub) => {
                          const subSelected = emailDocsSubTab === sub.value;
                          return (
                            <button
                              key={sub.value}
                              type="button"
                              onClick={() => onSelectEmailDocsSub(sub.value)}
                              className={cn(
                                'flex items-center gap-2 w-full py-1.5 rounded-md cursor-pointer hover:bg-muted transition-colors text-left text-foreground',
                                subSelected && 'bg-primary/10 font-medium text-primary'
                              )}
                            >
                              <ChevronRight className="h-4 w-4 shrink-0" />
                              <span className="text-sm flex-1">{t(sub.labelKey)}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {sheet && isVariables && (
                      <div className="pl-4 space-y-0.5 border-l border-border ml-2">
                        {VARIABLES_SUB_ITEMS.filter(
                          (sub) =>
                            !navFilterLower ||
                            labelMatchesFilter(sub.labelKey) ||
                            labelMatchesFilter('settings.catalogAndContent')
                        ).map((sub) => (
                          <button
                            key={sub.value}
                            type="button"
                            onClick={() => {
                              onCloseSheet?.();
                              onSelectTab('variables');
                              onSelectVariablesSub(sub.value);
                            }}
                            className={cn(
                              'flex w-full p-2 rounded-md text-left text-sm hover:bg-muted',
                              settingsTab === 'variables' &&
                                variablesSubTab === sub.value &&
                                'bg-primary/10 font-medium text-primary'
                            )}
                          >
                            {t(sub.labelKey)}
                          </button>
                        ))}
                      </div>
                    )}
                    {sheet && isEmailDocs && (
                      <div className="pl-4 space-y-0.5 border-l border-border ml-2">
                        {EMAIL_DOCS_SUB_ITEMS.filter(
                          (sub) =>
                            !navFilterLower ||
                            labelMatchesFilter(sub.labelKey) ||
                            labelMatchesFilter('settings.emailAndDocs')
                        ).map((sub) => (
                          <button
                            key={sub.value}
                            type="button"
                            onClick={() => {
                              onCloseSheet?.();
                              onSelectTab('email-and-docs');
                              onSelectEmailDocsSub(sub.value);
                            }}
                            className={cn(
                              'flex w-full p-2 rounded-md text-left text-sm hover:bg-muted',
                              settingsTab === 'email-and-docs' &&
                                emailDocsSubTab === sub.value &&
                                'bg-primary/10 font-medium text-primary'
                            )}
                          >
                            {t(sub.labelKey)}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </>
  );
}
